// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { AddressValidationModel } from "@gcg/App/Services/AddressValidationWithAvalaraService";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface AddressSuggestionProps {
    addressSuggestionModalIsOpen: boolean;
    addressSuggestionModalCloseHandler: () => void;
    selectedAddressSuggestionId: string;
    saveSelectedAddressSuggestion: (id: string | undefined) => void;
    addressSuggestionList: AddressValidationModel[];
    setSelectedAddressSuggestionId: (id: string) => void;
    addressValidationErrorModalIsOpen: boolean;
    addressValidationErrorModalCloseHandler: () => void;
    addressValidationError: string;
    addressSuggestionsSkip: () => void;
    title?: string;
}

export interface AddressSuggestionStyles {
    outerContainer?: InjectableCss;
    addressSuggestionModal?: ModalPresentationProps;
    addressValidationErrorModal?: ModalPresentationProps;
    addressSuggestionsWrapper: InjectableCss;
    addressValidationErrorsWrapper: InjectableCss;
    addressSuggestionsSkipButton: ButtonPresentationProps;
    addressSuggestionsSaveButton: ButtonPresentationProps;
    title?: TypographyPresentationProps;
}

export const styles: AddressSuggestionStyles = {
    outerContainer: {
        css: css`
            padding: 20px;
            align-items: center;
        `,
    },
    addressSuggestionModal: { sizeVariant: "small" },
    addressValidationErrorModal: { sizeVariant: "small" },

    addressSuggestionsWrapper: {
        css: css`
            margin-top: 30px !important;
        `,
    },
    addressSuggestionsSkipButton: {
        variant: "secondary",
    },
    addressSuggestionsSaveButton: {
        css: css`
            width: 100%;
            margin: 1px 0;
        `,
    },
    addressValidationErrorsWrapper: {
        css: css``,
    },
    title: {
        size: "16px",
        lineHeight: "20px",
        color: "text.main",
        css: css`
            margin-bottom: 28px;
        `,
    },
};

const AddressSuggestion: FC<AddressSuggestionProps> = ({
    addressSuggestionModalIsOpen,
    addressSuggestionModalCloseHandler,
    selectedAddressSuggestionId,
    saveSelectedAddressSuggestion,
    addressSuggestionList,
    setSelectedAddressSuggestionId,
    addressValidationError,
    addressValidationErrorModalIsOpen,
    addressValidationErrorModalCloseHandler,
    addressSuggestionsSkip,
    title,
}) => {
    const getAddressText = (address: AddressValidationModel) => {
        return `${address.addressLine1 || address.line1}, ${address.city}, ${address.state || address.region} ${
            address.postalCode
        }, ${address.country}`;
    };

    const setSelectedItem = (suggestion: AddressValidationModel) => {
        saveSelectedAddressSuggestion(suggestion.id);
    };
    return (
        <>
            <Modal
                headline={title ?? translate("Address Suggestions")}
                {...styles.addressSuggestionModal}
                isOpen={addressSuggestionModalIsOpen}
                handleClose={addressSuggestionsSkip}
            >
                <Typography as="h2" {...styles.title}>
                    {siteMessage("Custom_AddressSuggestionPopup_Header")}
                </Typography>
                {selectedAddressSuggestionId}
                {addressSuggestionList.map(validatedAddress => {
                    const value = getAddressText(validatedAddress);
                    return (
                        <Button
                            {...styles.addressSuggestionsSaveButton}
                            key={(validatedAddress as any).id}
                            type="button"
                            onClick={() => setSelectedItem(validatedAddress)}
                        >
                            {value}
                        </Button>
                    );
                })}
                <StyledWrapper {...styles.addressSuggestionsWrapper} className="modal-actions">
                    <Button {...styles.addressSuggestionsSkipButton} type="button" onClick={addressSuggestionsSkip}>
                        {translate("Skip")}
                    </Button>
                </StyledWrapper>
            </Modal>
            <Modal
                headline={siteMessage("Custom_AddressSuggestion_Modal_Title")}
                {...styles.addressValidationErrorModal}
                isOpen={addressValidationErrorModalIsOpen}
                handleClose={addressValidationErrorModalCloseHandler}
            >
                <StyledWrapper {...styles.addressValidationErrorsWrapper}>
                    <Typography>{addressValidationError}</Typography>
                </StyledWrapper>
            </Modal>
        </>
    );
};

export default AddressSuggestion;
